import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import { PARTNER_BRAND } from '../../partnerDashboardTheme';

const outlinedButtonSx = {
  minWidth: 88,
  height: 40,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 1.5,
  color: PARTNER_BRAND,
  borderColor: PARTNER_BRAND,
  '&:hover': {
    borderColor: 'primary.dark',
    bgcolor: 'rgba(224, 104, 10, 0.04)',
  },
};

const containedButtonSx = {
  minWidth: 88,
  height: 40,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 1.5,
  bgcolor: PARTNER_BRAND,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: 'primary.dark',
    boxShadow: 'none',
  },
};

const EditCommunityMessageDialog = ({
  open,
  initialText = '',
  saving = false,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (!open) return;
    setDraft(initialText);
  }, [open, initialText]);

  const trimmedDraft = draft.trim();
  const canSave = Boolean(trimmedDraft) && trimmedDraft !== String(initialText ?? '').trim();

  const handleSave = () => {
    if (!canSave || saving) return;
    onSave?.(trimmedDraft);
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          pb: 1,
          fontWeight: 700,
          fontSize: '1rem',
        }}
      >
        Edit message
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <TextField
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          autoFocus
          disabled={saving}
          placeholder="Update your message..."
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              handleSave();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              fontSize: '0.875rem',
              '& fieldset': { borderColor: 'divider' },
              '&:hover fieldset': { borderColor: 'text.disabled' },
              '&.Mui-focused fieldset': { borderColor: PARTNER_BRAND },
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={outlinedButtonSx}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={!canSave}
          onClick={handleSave}
          sx={containedButtonSx}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditCommunityMessageDialog;
